import { useEffect, useState } from "react";
import { useLocation, useParams } from "wouter";
import { useForm } from "react-hook-form";
import api from "@/lib/api";
import { ImageUpload } from "@/components/admin/ImageUpload";
import { ArrowLeft } from "lucide-react";
import { Link } from "wouter";

interface FormData {
  slug: string;
  name: string;
  category: string;
  url: string;
  image: string;
  about: string;
  solution: string;
  technology: string;
  sortOrder: number;
}

export default function ProjectForm() {
  const params = useParams<{ id: string }>();
  const isEdit = params.id && params.id !== "new";
  const [, navigate] = useLocation();
  const [saving, setSaving] = useState(false);

  const { register, handleSubmit, setValue, watch, reset } = useForm<FormData>({
    defaultValues: {
      slug: "",
      name: "",
      category: "",
      url: "",
      image: "",
      about: "",
      solution: "",
      technology: "",
      sortOrder: 0,
    },
  });

  const imageValue = watch("image");

  useEffect(() => {
    if (isEdit) {
      api.get(`/projects`).then(({ data }) => {
        const project = data.find((p: any) => p.id === parseInt(params.id!));
        if (project) reset(project);
      });
    }
  }, [isEdit, params.id, reset]);

  const onSubmit = async (data: FormData) => {
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/projects/${params.id}`, data);
      } else {
        await api.post("/projects", data);
      }
      navigate("/admin/projects");
    } catch (err: any) {
      alert(err.response?.data?.error || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <Link href="/admin/projects" className="text-white/40 hover:text-white transition-colors">
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <h1 className="text-2xl font-bold text-white font-[Rubik]">
          {isEdit ? "Edit Project" : "New Project"}
        </h1>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="max-w-3xl space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-white/60 mb-1.5">Name</label>
            <input
              {...register("name")}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:border-[#66CDAA]/50 outline-none"
              dir="rtl"
              required
            />
          </div>
          <div>
            <label className="block text-sm text-white/60 mb-1.5">Slug</label>
            <input
              {...register("slug")}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:border-[#66CDAA]/50 outline-none"
              dir="ltr"
              required
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm text-white/60 mb-1.5">Category</label>
            <input
              {...register("category")}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:border-[#66CDAA]/50 outline-none"
              dir="rtl"
              required
            />
          </div>
          <div>
            <label className="block text-sm text-white/60 mb-1.5">URL</label>
            <input
              {...register("url")}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:border-[#66CDAA]/50 outline-none"
              dir="ltr"
            />
          </div>
          <div>
            <label className="block text-sm text-white/60 mb-1.5">Sort order</label>
            <input
              type="number"
              {...register("sortOrder", { valueAsNumber: true })}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:border-[#66CDAA]/50 outline-none"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm text-white/60 mb-1.5">Image</label>
          <ImageUpload value={imageValue} onChange={(url) => setValue("image", url)} />
        </div>

        <div>
          <label className="block text-sm text-white/60 mb-1.5">About</label>
          <textarea
            {...register("about")}
            rows={3}
            className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:border-[#66CDAA]/50 outline-none resize-none"
            dir="rtl"
            required
          />
        </div>

        <div>
          <label className="block text-sm text-white/60 mb-1.5">Solution</label>
          <textarea
            {...register("solution")}
            rows={3}
            className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:border-[#66CDAA]/50 outline-none resize-none"
            dir="rtl"
            required
          />
        </div>

        <div>
          <label className="block text-sm text-white/60 mb-1.5">Technology</label>
          <textarea
            {...register("technology")}
            rows={2}
            className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:border-[#66CDAA]/50 outline-none resize-none"
            dir="rtl"
            required
          />
        </div>

        <div className="flex gap-3">
          <button
            type="submit"
            disabled={saving}
            className="bg-[#66CDAA] hover:bg-[#57b899] text-[#0F172A] font-bold px-6 py-3 rounded-xl transition-colors disabled:opacity-50"
          >
            {saving ? "Saving..." : isEdit ? "Update" : "Create"}
          </button>
          <Link
            href="/admin/projects"
            className="px-6 py-3 rounded-xl border border-white/10 text-white/50 hover:text-white hover:border-white/20 transition-colors"
          >
            Cancel
          </Link>
        </div>
      </form>
    </div>
  );
}
